import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IDeliverableFormat } from "@shared/lib/types";
import { Button } from "@shared/ui/Button";
import {
  Card,
  Divider,
  Flex,
  Form,
  Input,
  List,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import useCreateDeliverableFormat from "../../../entities/deliverableConfigurator/lib/hooks/useCreateDeliverableFormat";
import { ToastService } from "@shared/lib/services";
import useUpdateDeliverableFormat from "../../../entities/deliverableConfigurator/lib/hooks/useUpdateDeliverableFormat";
import FormatSubTypeCard from "@entities/deliverableConfigurator/ui/FormatSubTypeCard";

const { TextArea } = Input;

export default function FormatForm({
  deliverableId,
  format,
  onClose,
  isEdit,
}: {
  deliverableId: string;
  format: IDeliverableFormat | null;
  onClose: () => void;
  isEdit: boolean;
}) {
  const [form] = Form.useForm();

  const {
    createDeliverableFormat,
    createDeliverableFormatError,
    isCreateDeliverableFormatLoading,
    isCreateDeliverableFormatSuccess,
  } = useCreateDeliverableFormat();

  const {
    updateDeliverableFormat,
    updateDeliverableFormatError,
    isUpdateDeliverableFormatLoading,
    isUpdateDeliverableFormatSuccess,
    updateDeliverableFormatVariables,
  } = useUpdateDeliverableFormat();

  useEffect(() => {
    if (createDeliverableFormatError) {
      ToastService.error(createDeliverableFormatError?.message);
      onClose();
    } else if (isCreateDeliverableFormatSuccess) {
      ToastService.success("Deliverable format added successfully.");
    }

    if (updateDeliverableFormatError) {
      ToastService.error(updateDeliverableFormatError?.message);
    } else if (isUpdateDeliverableFormatSuccess) {
      ToastService.success("Deliverable format updated successfully.");
    }
  }, [
    isCreateDeliverableFormatSuccess,
    createDeliverableFormatError,
    isUpdateDeliverableFormatSuccess,
    updateDeliverableFormatError,
  ]);

  const updateFormat = async () => {
    try {
      await form.validateFields(["category", "description"]);
      updateDeliverableFormat({
        deliverableConfiguratorId: deliverableId,
        deliverableConfiguratorDeliverableId: format?._id || "",
        description: form.getFieldValue("description"),
        category: form.getFieldValue("category"),
      });
    } catch (err) {
      console.log("Error");
    }
  };

  const addFormat = () => {
    createDeliverableFormat({
      deliverableConfiguratorId: deliverableId,
      deliverable: { ...form.getFieldsValue() },
    });
  };

  return (
    <Form
      form={form}
      initialValues={
        format
          ? {
              ...format,
            }
          : {
              subTypes: [
                {
                  name: "",
                  description: "",
                  values: [],
                },
              ],
            }
      }
      layout="vertical"
      onFinish={() => addFormat()}
    >
      <Form.Item
        name={"category"}
        label="Format Name"
        rules={[{ required: true, message: "Enter format name" }]}
      >
        <Input placeholder="e.g. Story, Feed, Short" />
      </Form.Item>

      <Form.Item name={"description"} label="Format Description">
        <TextArea rows={2} placeholder="Describe the format" />
      </Form.Item>
      {isEdit && (
        <Flex gap={10} vertical>
          <Button
            type="primary"
            loading={
              !updateDeliverableFormatVariables?.addSubType &&
              isUpdateDeliverableFormatLoading
            }
            onClick={() => updateFormat()}
          >
            Submit
          </Button>
          <Divider />
        </Flex>
      )}
      <Form.List name={"subTypes"}>
        {(subFields, { add: addSub, remove: removeSub }) => (
          <>
            <Flex
              justify="space-between"
              align="center"
              style={{ margin: "20px 0" }}
            >
              <Typography.Title level={5} style={{ margin: 0 }}>
                SubTypes
              </Typography.Title>
              <Form.Item noStyle>
                <Button
                  type="dashed"
                  onClick={() => {
                    addSub({
                      name: "",
                      description: "",
                      values: [],
                    });
                    const index = subFields.length;
                    setTimeout(() => {
                      form.scrollToField(["subTypes", index, "name"], {
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 100);
                  }}
                  icon={<PlusOutlined />}
                >
                  Add SubType
                </Button>
              </Form.Item>
            </Flex>
            <List
              dataSource={subFields}
              style={{ maxHeight: 400, overflow: "auto" }}
              renderItem={(subField) => (
                <List.Item key={subField.name}>
                  <FormatSubTypeCard
                    deliverableId={deliverableId}
                    subField={subField}
                    format={format as IDeliverableFormat}
                    isEdit={isEdit}
                    removeSub={removeSub}
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Form.List>
      {!isEdit && (
        <Form.Item>
          <Flex gap={10} justify="flex-end" style={{ marginTop: 20 }}>
            <Button
              onClick={() => {
                form.resetFields();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              loading={isCreateDeliverableFormatLoading}
              type="primary"
              htmlType="submit"
            >
              Submit
            </Button>
          </Flex>
        </Form.Item>
      )}
    </Form>
  );
}
